import React, { useContext, useEffect, useCallback, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { document, sectionApi, titleOpinionApi } from '~/api';
import { createNamedContext } from '~/util';
import { useRouter } from '~/hooks';
import debounce from 'lodash/debounce';

/**
 * @typedef TitleOpinionContext
 * @property {TitleOpinion} titleOpinion
 * @property {boolean} existingOpinion Indicates whether this title opinion is in our database
 */

/**@type {TitleOpinionContext} */
export const titleOpinionContext = createNamedContext('TitleOpinionContext');

/**
 * Hook for managing Title Opinion state
 * 
 * @returns {TitleOpinionContext} TitleOpinion State
 */
export const useTitleOpinion = () => useContext(titleOpinionContext);

export const withTitleOpinion = (Component) => {
    const ctx = useTitleOpinion();
    return <Component {...ctx} />;
};

export const ProvideTitleOpinion = ({ children }) => {
    const { id } = useParams();

    const opinion = useProvideTitleOpinion(id);
    return (
        <titleOpinionContext.Provider value={opinion}>
            {children}
        </titleOpinionContext.Provider>
    );
};

/**
 * 
 * @param {Number} id Title Opinion Id
 * @returns {TitleOpinionContext}
 */
export function useProvideTitleOpinion(id = 0) {
    const [titleOpinion, setTitleOpinion] = useState(null);

    const loadOpinion = async () => {
        if (id != 0) {
            const opinion = await titleOpinionApi.getTitleOpinion(id);

            setTitleOpinion(opinion);
        }
    };

    useEffect(() => loadOpinion(), [id]);

    const existingOpinion = useMemo(
        () => titleOpinion && Object.keys(titleOpinion).length > 0,
        [titleOpinion]);

    return {
        titleOpinion,
        existingOpinion
    };
}


/**
 * @typedef Section
 * @property {number} sectionId The Section's id
 * @property {number} titleOpinionId The id of the associated titleOpinion
 * @property {object} _data The section's persisted data (e.g., form state)
 * 
 * @typedef SectionContext
 * @property {() => Promise<Section>} loadSection Refreshes the @see section property
 * @property {string} saveStatus The asyncState of the saveField operation
 * @property {(path: string, value: any, sectionId: any) => Promise<Section>} saveField Updates the data property such that _data[path] = value
 */


/**
 * 
 * Returns an `onLoad` function that can be passed to `useForm` for initializing form state
 * 
 * This function will spread the section's `_data` and Id over the `values` object it recieves
 * @param {number} sectionType The section type identifier
 * @param {number} titleOpinionId The id of the parent title opinion
 * @param {number} additionalSectionType The id of the parent title opinion
 * @returns {{onLoad: (values: object) => Promise<object>}} A prebuild `onLoad` function
 */
export function useSectionLoader(sectionType, titleOpinionId, additionalSectionType) {    

    const load = (sectionType, titleOpinionId = 0, additionalSectionType) => async (values) => {
        const data = { ...values };   

        if (titleOpinionId != 0) {
            const section = await sectionApi.getSection(titleOpinionId, sectionType);
            const details = await sectionApi.getSectionDetail(titleOpinionId, sectionType);
            const additionalSection = additionalSectionType ? await sectionApi.getSection(titleOpinionId, additionalSectionType) : null;

            let documents = [];

            if (Array.isArray(section.documents)) {
                documents = section.documents
                    .map(d => {
                        return {
                            file: {
                                path: d.name
                            },
                            type: d.typeId,
                            uploaded: true,
                            isDeleted: false,
                            ...d
                        };
                    });
            }

            return {
                ...data,
                sectionId: section.sectionId,
                orderNumber: section.orderNumber ? section.orderNumber : null,
                errorMessage: section.errorMessage ? section.errorMessage : null,
                ...section._data,
                sectionDetails: details,
                additionalSection: additionalSectionType ? additionalSection._data : null,
                documents
            };
        }
        return data;
    };

    const onLoad = useCallback(load(sectionType, titleOpinionId, additionalSectionType), [sectionType, titleOpinionId]);

    return {
        onLoad
    };
}

/**
 * 
 * @typedef AutoSaver
 * @property {(value: any, path: string, stateValues: object, id: number?) => Promise<object>} saveField
 * @property {(doc: object, docType: number, id: number) => Promise<object>} saveDocument
 * @property {(docId: number, id: number) => Promise<void>} deleteDocument
 * Returns a debounced function for persisting form data in real time
 * 
 * @param {number} sectionType 
 * @param {number} titleOpinionId 
 * @param {number} sectionId 
 * @returns {AutoSaver} 
 */
export function useAutoSave(sectionType, titleOpinionId = 0, stateValues, sectionId = 0) {
    const { replace, pathname } = useRouter();
    
    const reload = useCallback((s) => {
        if (titleOpinionId == 0) {
            replace(pathname.replace('/0/',`/${s.titleOpinionId}/`));
        }else{
            replace(pathname);
        }
    }, [titleOpinionId]);

    const createSection = useCallback(async (sectionTypeId, path, value) => {
        const args = {
            sectionTypeId,
            titleOpinionId,
            path,
            value
        };

        return sectionApi.createSection(args);
    }, [titleOpinionId]);

    const fieldSaver = useCallback(async (value, path, stateValues, id = 0) => {
        const _sectionId = sectionId > 0 ? sectionId : id;
        if (_sectionId > 0) {
            await sectionApi.updateSection({ path, value, id: sectionId });
        } else {
            const newSection = await createSection(sectionType, path, value);

            if(newSection && newSection.sectionId && stateValues && Object.keys(stateValues).length > 0) {
                for (const [key, value] of Object.entries(stateValues)) {
                    if(key !== path) {
                        await sectionApi.updateSection({ path: key, value, id: newSection.sectionId });
                    }
                }
            }
            reload(newSection);
        }
    }, [sectionId, titleOpinionId, sectionType]);    

    const debounced = debounce(fieldSaver, 250);

    const saveDocument = useCallback(async (doc, docType, id) => {
        const _sectionId = sectionId > 0 ? sectionId : id;

        if (_sectionId > 0) {
            console.log(`File type ${docType} - ${JSON.stringify(doc)}`);

            await document.upload(doc);
            const attachment = { ...doc.uploadInfo, typeId: docType };
            await sectionApi.addSectionDocument(_sectionId, attachment);

            reload();
        }

    }, [sectionId, titleOpinionId, sectionType]);

    const deleteDocument = useCallback(async (docId, id) => {
        const _sectionId = sectionId > 0 ? sectionId : id;

        if (_sectionId > 0) {
            await sectionApi.deleteSectionDocument(_sectionId, docId);
            reload();
        }
    }, [sectionId, titleOpinionId, sectionType]);

    return { 
        saveField: debounced,
        saveDocument,
        deleteDocument
    };
}

/**
 * 
 * @typedef AutoSaver
 * @property {(data: object, stateValues: object, id: number?) => Promise<object>} saveMulitpleField
 * Returns a debounced function for persisting form data in real time
 * 
 * @param {number} sectionType 
 * @param {number} titleOpinionId 
 * @param {number} sectionId 
 * @returns {AutoSaver} 
 */
export function useAutoSaveMultiple(sectionType, titleOpinionId = 0, stateValues, sectionId = 0, reloadPage = true) {
    
    const { replace, pathname } = useRouter();
    
    const reload = useCallback((s) => {
        if (titleOpinionId == 0) {
            replace(pathname.replace('/0/',`/${s.titleOpinionId}/`));
        }else{
            replace(pathname);
        }
    }, [titleOpinionId]);
    
    const multipleFieldSaver = useCallback(async (values, stateValues, id = 0) => {

        const _sectionId = sectionId > 0 ? sectionId : id;
        if (_sectionId > 0 && values && Object.keys(values).length > 0 ) {
            for (const [key, value] of Object.entries(values)) {
                await sectionApi.updateSection({ path: key, value, id: _sectionId });
            }
        } else {            
            // Create New Section
            if(values && Object.keys(values).length > 0) {
                const newSectionArgs = {
                    sectionTypeId: sectionType,
                    titleOpinionId,
                    path: Object.keys(values)[0],
                    value: Object.values(values)[0]
                };
                const newSection = await sectionApi.createSection(newSectionArgs);
                for (const [key, value] of  Object.entries(values)) {
                    if(newSection && newSection.sectionId) {
                        await sectionApi.updateSection({ path: key, value, id: newSection.sectionId });
                    }
                }
                if(newSection && newSection.sectionId && stateValues && Object.keys(stateValues).length > 0) {
                    for (const [key, value] of Object.entries(stateValues)) {
                        await sectionApi.updateSection({ path: key, value, id: newSection.sectionId });
                    }
                }
                if(reloadPage)
                    reload(newSection);                
            }
        }
    }, [sectionId, titleOpinionId, sectionType]);    

    const debounced = debounce(multipleFieldSaver, 250);

    return { saveMulitpleField: debounced };
}

/**
 * 
 * @typedef AutoSaver
 * @property {(value: any, sectionType: number, stateValues: object, titleOpinionId: number, id: number?) => Promise<object>} saveField
 * Returns a debounced function for persisting form data in real time
 * 
 * @param {number} sectionId 
 * @param {number} sectionType
 * @param {object} stateValues
 * @param {number} titleOpinionId
 * @param {number} sectionDetailId 
 * @returns {AutoSaver} 
 */
export function useSaveSectionDetail(sectionId, sectionType, stateValues , titleOpinionId, sectionDetailId) {

    const { replace, pathname } = useRouter();

    const reload = useCallback((s) => {
        if (titleOpinionId == 0) {
            replace(pathname.replace('/0/',`/${s.titleOpinionId}/`));
        }else{
            replace(pathname);
        }
    }, [titleOpinionId]);

    const sectionDetailSave = useCallback(async (value , sectionType , stateValues , titleOpinionId, id = 0) => {        
        
        if(!value.sectionId || value.sectionId === 0) {            
            const newSectionArgs = {
                sectionTypeId: sectionType,
                titleOpinionId,
                path: stateValues ? Object.keys(stateValues)[0] : sectionType,
                value: stateValues ? Object.values(stateValues)[0] : sectionType
            };
            const newSection = await sectionApi.createSection(newSectionArgs);
            value.sectionId = newSection.sectionId;
            if(newSection && newSection.sectionId && stateValues && Object.keys(stateValues).length > 0) {
                for (const [key, value] of Object.entries(stateValues)) {
                    await sectionApi.updateSection({ path: key, value, id: newSection.sectionId });
                }
            }
            const _sectionDetailId = sectionId > 0 ? sectionDetailId : id;
            sectionApi.saveSectionDetail(value, _sectionDetailId).then(() => {
                reload(newSection);
            });
        } else {
            const _sectionDetailId = sectionId > 0 ? sectionDetailId : id;
            sectionApi.saveSectionDetail(value, _sectionDetailId).then(() => {
                replace(pathname);
            });
        }       
        
    }, [sectionDetailId, sectionId, titleOpinionId]);    

    const debounced = debounce(sectionDetailSave, 250);
    return { saveField: debounced };
}


/**
 * 
 * @typedef DeleteSectionDetail
 * @property {(id: number?) => Promise<object>} deleteSectionDetail
 * Returns a debounced function for persisting form data in real time
 * 
 * @param {number} sectionDetailId 
 */
export function useDeleteSectionDetail(sectionDetailId) {

    const { replace, pathname } = useRouter();
    const deleteSectionDetail = useCallback(async (id = 0) => {
        sectionApi.deleteSectionDetail(id).then(() => {
            replace(pathname);
        });
        
    }, [sectionDetailId]);    

    const debounced = debounce(deleteSectionDetail, 250);
    return { deleteSectionDetail: debounced };
}


/**
 * 
 * @typedef DeleteSectionDetail
 * @property {(sectionDetails: array?) => Promise<object>} deleteEntireSectionDetail
 * Returns a debounced function for persisting form data in real time
 * 
 * @param {array} sectionDetails 
 */
export function useDeleteEntireSectionDetail() {

    const { replace, pathname } = useRouter();

    const deleteEntireSectionDetail = useCallback(async (sectionDetails, loadSection = true) => {
        if(sectionDetails && sectionDetails.length > 0){
            await sectionDetails.map(f => sectionApi.deleteSectionDetail(f.sectionDetailId));
        }  
        if(loadSection) {
            replace(pathname);   
        }                       
    }, []);    

    const debounced = debounce(deleteEntireSectionDetail, 250);
    return { deleteEntireSectionDetail: debounced };
}

/**
 * 
 * @typedef submitReviewForm
 * @property {(id: number?, sectionType: number?) => Promise<object>} submitReviewForm
 */
export async function useSubmitReviewForm(titleOpinionId, sectionType) {
    const section = titleOpinionId != 0 ? await sectionApi.getSection(titleOpinionId, sectionType) : '';  
    return section._data;
}
